const WORDS_PER_MINUTE = 200;

/**
 * The subset of a `LessonsPage.tsx` lesson entry this estimate actually reads.
 * Both fields are plain prose strings rendered as `<p>` body text on the
 * lesson card - the only free-text fields every lesson entry carries (every
 * lesson has `paragraphs`; only a few also have `paragraphsAfterFigure`, the
 * continuation shown after the lesson's figure).
 */
export interface LessonReadingContent {
    paragraphs?: string[];
    paragraphsAfterFigure?: string[];
}

/**
 * Estimates a lesson's reading time, the same way `estimateReadingMinutes` in
 * `blogReadingTime.ts` estimates a blog post's: total word count (runs of
 * whitespace collapsed to a single gap) divided by a 200 words/minute
 * constant, rounded to the nearest whole minute and floored at 1 so no
 * lesson ever reads "0 min".
 *
 * What this counts: `paragraphs` and, when present, `paragraphsAfterFigure` -
 * the lesson's own explanatory prose, in whichever language is already
 * active on the lesson object (callers should pass the already-localized
 * lesson so a Portuguese learner sees a Portuguese-text-based estimate, the
 * same way the blog computes reading time from `blogPostContentPt` rather
 * than the English `post.content`).
 *
 * What this deliberately does NOT count, to keep the estimate well-defined
 * and consistently applied across all ~77 lessons rather than ad hoc per
 * lesson:
 *  - `title`/`searchTerms` - metadata, not prose rendered on the card
 *    (mirrors blogReadingTime.ts, which also excludes the post title).
 *  - `figure`/`charts` - JSX diagrams, tables, worked examples and quizzes
 *    (e.g. `<HiraganaChartsSection />`). These often carry most of a
 *    lesson's actual content, but their text only exists once the
 *    component is rendered, so counting it would make this neither a pure
 *    nor a reliably testable function of a single lesson object.
 *  - `practice.label` - a call-to-action link, not lesson content (mirrors
 *    the blog's own "Read more" link going uncounted).
 *  - the optional per-lesson culture notes in `consts/lessonCultureNotes.ts`
 *    - a separate data source keyed by lesson number rather than a field on
 *    the lesson object itself, so counting it would require this helper to
 *    take on a second data dependency. Measured for real lesson content as
 *    of writing, including every culture note would not change the
 *    resulting minute for all but a literal handful of lessons, so it was
 *    left out to keep this a pure function of one lesson object; revisit if
 *    culture notes grow into a bigger share of lesson content.
 *
 * Because most grammar-point lessons are intentionally short explainers -
 * the bulk of a lesson's study time is spent on the practice/reference
 * sections this helper doesn't count - most lessons will floor to "1 min
 * read"; only the handful with substantial standalone prose (e.g. the early
 * "What is kana?"/"What is romaji?" lessons) read as more. That's an
 * accurate reflection of what's actually being measured (the explanatory
 * reading), not of total time spent on the lesson.
 */
export function estimateReadingMinutes(lesson: LessonReadingContent): number {
    const paragraphs = [...(lesson.paragraphs ?? []), ...(lesson.paragraphsAfterFigure ?? [])];
    const wordCount = paragraphs.reduce((total, paragraph) => {
        const text = paragraph.trim();
        return total + (text ? text.split(/\s+/).length : 0);
    }, 0);
    return Math.max(1, Math.round(wordCount / WORDS_PER_MINUTE));
}
